import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import type { Suggestion } from "react-places-autocomplete";
import { useTranslation } from "react-i18next";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { GOOGLE_KEY } from "services/baseURLs.config";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { isPreciseAddressSuggestion } from "./locationMap.logic";
import {
  LocationAutocompleteContainer,
  LoadingWrapper,
  LocationInput,
  LocationInputWrapper,
  SuggestionContainer,
} from "./locationMap.styled";

/** Keeps Google from suggesting cities, regions and other non-addresses. */
const ADDRESS_SEARCH_OPTIONS = { types: ["address"] };

let locationListboxCount = 0;

type Props = {
  address?: string;
  hasIncompleteAddress?: boolean;
  inRegistration?: boolean;
  noMap?: boolean;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  setPostalCode: React.Dispatch<React.SetStateAction<string>>;
};

export const LocationMap: React.FC<Props> = ({
  address,
  setAddress,
  setCity,
  setPostalCode,
  setLocation,
  hasIncompleteAddress,
  inRegistration,
  noMap,
}) => {
  const [libraries] = useState<["places"]>(["places"]);
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_KEY,
    libraries: libraries,
  });
  const [companyState] = useCompanyStore();
  const [addressString, setAddressString] = useState<string | null>("");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [validAddress, setValidAddress] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isAddressSelectionPending, setIsAddressSelectionPending] =
    useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [isSuggestionListOpen, setIsSuggestionListOpen] = useState(false);
  const listboxIdRef = useRef<string>();
  const selectionRequestIdRef = useRef(0);

  if (!listboxIdRef.current) {
    locationListboxCount += 1;
    listboxIdRef.current = `location-suggestions-${locationListboxCount}`;
  }

  const listboxId = listboxIdRef.current;
  const getOptionId = (index: number): string => `${listboxId}-option-${index}`;
  const hasUserEditedAddressRef = useRef(false);
  const hasSelectedSuggestionRef = useRef(false);
  const companyAddress = companyState.company?.address;
  const companyCity = companyState.company?.city;
  const companyLocation = companyState.company?.location;

  useEffect(() => {
    if (!inRegistration) {
      hasUserEditedAddressRef.current = false;
      return;
    }
    if (hasUserEditedAddressRef.current) return;
    setAddressString(address ?? "");
    hasSelectedSuggestionRef.current = false;
    setIsAddressSelectionPending(false);
  }, [address, inRegistration]);

  useEffect(() => {
    if (inRegistration) return;

    if (companyLocation) {
      const [nextLat, nextLng] = companyLocation
        .split(",")
        .map((coordinate) => Number(coordinate.trim()));
      setLat(Number.isFinite(nextLat) ? nextLat : null);
      setLng(Number.isFinite(nextLng) ? nextLng : null);
    } else {
      setLat(null);
      setLng(null);
    }

    if (companyAddress) {
      setAddressString(
        companyCity ? `${companyAddress}, ${companyCity}` : companyAddress,
      );
    } else {
      setAddressString("");
    }
  }, [companyAddress, companyCity, companyLocation, inRegistration]);

  const clearResolvedLocation = (): void => {
    setLat(null);
    setLng(null);
    setAddress("");
    setCity("");
    setPostalCode("");
    setLocation("");
  };

  const markAddressInvalid = (): void => {
    clearResolvedLocation();
    setValidAddress(false);
    setIsAddressSelectionPending(false);
  };

  const handleAddressChange = (nextAddress: string): void => {
    hasUserEditedAddressRef.current = true;
    hasSelectedSuggestionRef.current = false;
    selectionRequestIdRef.current += 1;
    setActiveSuggestionIndex(0);
    setIsSuggestionListOpen(true);
    setAddressString(nextAddress);
    clearResolvedLocation();
    setValidAddress(true);
    setIsAddressSelectionPending(false);
  };

  const handleAddressBlur = (activeSuggestion?: Suggestion): void => {
    setIsFocused(false);
    setIsSuggestionListOpen(false);

    // Leaving the field with a suggestion highlighted picks it, so the address
    // does not have to be confirmed from the list to count as chosen.
    if (
      activeSuggestion &&
      hasUserEditedAddressRef.current &&
      !hasSelectedSuggestionRef.current
    ) {
      void handleSelect(activeSuggestion.description);
      return;
    }

    setIsAddressSelectionPending(
      hasUserEditedAddressRef.current &&
        Boolean(addressString?.trim()) &&
        !hasSelectedSuggestionRef.current,
    );
  };

  const handleSelect = async (selectedAddress: string): Promise<void> => {
    hasUserEditedAddressRef.current = true;
    hasSelectedSuggestionRef.current = true;
    setActiveSuggestionIndex(0);
    setIsSuggestionListOpen(false);
    setIsAddressSelectionPending(false);
    selectionRequestIdRef.current += 1;
    const requestId = selectionRequestIdRef.current;

    try {
      const results = await geocodeByAddress(selectedAddress);
      const firstResult = results[0];

      if (selectionRequestIdRef.current !== requestId) return;
      if (!firstResult) {
        markAddressInvalid();
        return;
      }

      const latlng = await getLatLng(firstResult);
      if (selectionRequestIdRef.current !== requestId) return;

      const streetNumber = firstResult.address_components.find((component) =>
        component.types.includes("street_number"),
      );
      const street =
        firstResult.address_components.find((component) =>
          component.types.includes("route"),
        ) ?? firstResult.address_components[0];
      const postalTown =
        firstResult.address_components.find((component) =>
          component.types.includes("postal_town"),
        ) ??
        firstResult.address_components.find((component) =>
          component.types.includes("locality"),
        );
      const postalCode = firstResult.address_components.find((component) =>
        component.types.includes("postal_code"),
      );

      if (
        !Number.isFinite(latlng.lat) ||
        !Number.isFinite(latlng.lng) ||
        !streetNumber?.long_name ||
        !postalTown?.long_name ||
        !postalCode?.long_name
      ) {
        markAddressInvalid();
        return;
      }

      setLat(latlng.lat);
      setLng(latlng.lng);
      setLocation(`${latlng.lat},${latlng.lng}`);
      setValidAddress(true);
      setAddressString(firstResult.formatted_address);
      setAddress(`${street?.long_name ?? ""} ${streetNumber.long_name}`.trim());
      setCity(postalTown.long_name);
      setPostalCode(postalCode.long_name.replace(/\s+/g, ""));
      setIsAddressSelectionPending(false);
    } catch {
      if (selectionRequestIdRef.current === requestId) {
        markAddressInvalid();
      }
    }
  };

  const addressErrorText = !validAddress
    ? t("ProfileCompany.InvalidAddress")
    : isAddressSelectionPending || hasIncompleteAddress
      ? t("RegisterCompany.LocationHelperText")
      : null;

  return isLoaded ? (
    <>
      <PlacesAutocomplete
        value={addressString || ""}
        onChange={handleAddressChange}
        onSelect={handleSelect}
        searchOptions={ADDRESS_SEARCH_OPTIONS}
      >
        {({ getInputProps, suggestions, loading, getSuggestionItemProps }) => {
          const preciseSuggestions = isSuggestionListOpen
            ? suggestions.filter(isPreciseAddressSuggestion)
            : [];
          const activeIndex =
            preciseSuggestions.length > 0
              ? Math.min(activeSuggestionIndex, preciseSuggestions.length - 1)
              : -1;
          const activeSuggestion = preciseSuggestions[activeIndex];
          const inputProps = getInputProps({
            placeholder: t("ProfileCompany.LocationPlaceholder"),
            onFocus: () => setIsFocused(true),
            onBlur: () => handleAddressBlur(activeSuggestion),
          });

          const handleKeyDown = (
            event: React.KeyboardEvent<HTMLInputElement>,
          ): void => {
            if (event.key === "Enter" && activeSuggestion) {
              event.preventDefault();
              void handleSelect(activeSuggestion.description);
              return;
            }

            if (event.key === "Escape") {
              setIsSuggestionListOpen(false);
              inputProps.onKeyDown(event);
              return;
            }

            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();

              if (preciseSuggestions.length === 0) {
                return;
              }

              const step = event.key === "ArrowDown" ? 1 : -1;
              setActiveSuggestionIndex(
                (activeIndex + step + preciseSuggestions.length) %
                  preciseSuggestions.length,
              );
              return;
            }

            inputProps.onKeyDown(event);
          };

          return (
            <LocationAutocompleteContainer>
              <LocationInputWrapper>
                <LocationInput
                  {...inputProps}
                  onKeyDown={handleKeyDown}
                  aria-expanded={preciseSuggestions.length > 0}
                  aria-controls={listboxId}
                  aria-activedescendant={
                    activeIndex >= 0 ? getOptionId(activeIndex) : undefined
                  }
                  $isFocused={isFocused}
                  $hasError={!validAddress}
                />
                {loading && (
                  <LoadingWrapper>
                    <CircularProgress size={20} color="inherit" />
                  </LoadingWrapper>
                )}
              </LocationInputWrapper>
              {addressErrorText && (
                <Text color={ColorV2.NegativeRed} typography="s1">
                  {addressErrorText}
                </Text>
              )}
              <div
                className="autocomplete-dropdown-container"
                id={listboxId}
                role="listbox"
              >
                {preciseSuggestions.map((suggestion, index) => {
                  return (
                    <SuggestionContainer
                      {...getSuggestionItemProps(suggestion, {
                        onMouseEnter: () => setActiveSuggestionIndex(index),
                      })}
                      key={suggestion.placeId}
                      id={getOptionId(index)}
                      role="option"
                      aria-selected={index === activeIndex}
                      $isActive={index === activeIndex}
                    >
                      <Text color={ColorV2.Blue4} typography="b2">
                        {suggestion.description}
                      </Text>
                    </SuggestionContainer>
                  );
                })}
              </div>
            </LocationAutocompleteContainer>
          );
        }}
      </PlacesAutocomplete>
      {!noMap && (
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "300px",
          }}
          center={{ lat: lat || 59.334591, lng: lng || 18.06324 }}
          zoom={15}
        >
          {lat && lng && (
            <Marker
              title={companyState.company?.companyName || ""}
              position={{ lat: lat, lng: lng }}
            />
          )}
        </GoogleMap>
      )}
    </>
  ) : (
    <>
      <CircularProgress />
    </>
  );
};
